import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { ApiError, api, can, refusalReason, shortDate, type Questionnaire } from '../lib/api'
import { DataTable, ErrorBox, Loading, type Column } from '../components/DataTable'
import { StandardTemplates } from '../components/StandardTemplates'
import '../styles/studio.css'

// Purpose is not decoration. Publishing an ASSESSMENT version runs a
// scoreability check that refuses a version whose questions no bound model
// weighs; the other two purposes declare, up front, that no model reads this
// set. Choosing wrong here is the mistake that surfaces weeks later as a
// publish nobody can complete.
const PURPOSES: { value: string; label: string; detail: string }[] = [
  {
    value: 'ASSESSMENT',
    label: 'Assessment',
    detail: 'Scored. A risk model reads these answers, and publishing checks that every weighted key exists.',
  },
  {
    value: 'INFORMATIONAL',
    label: 'Informational',
    detail: 'Collected and stored, never scored. This is the honest way to gather context.',
  },
  {
    value: 'PROFILING',
    label: 'Profiling',
    detail: 'Feeds the inherent tier through the vendor profile rather than through weights.',
  },
]

function statusPill(q: Questionnaire) {
  if (q.isArchived) {
    return <span className="pill pill-off">Archived</span>
  }
  if (q.draftVersionId) {
    return <span className="pill pill-pending">Draft v{q.draftVersionNo} open</span>
  }
  if (q.publishedVersionId) {
    return <span className="pill pill-on">Published v{q.publishedVersionNo}</span>
  }
  return <span className="pill pill-off">No version yet</span>
}

export function Questionnaires() {
  const [list, setList] = useState<Questionnaire[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  // Archived questionnaires are listed here and nowhere else, one press away
  // (review 3.2): they stay because every answer given against them cites
  // their questions, and they are out of the way because nobody picks them.
  const [showArchived, setShowArchived] = useState(false)

  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [purpose, setPurpose] = useState('ASSESSMENT')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)

  const load = useCallback(() => {
    api
      .allQuestionnaires()
      .then((r) => {
        setList(r.questionnaires)
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])

  function create(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaveError(null)
    api
      .createQuestionnaire({ code: code.trim(), name: name.trim(), description, purpose })
      .then((r) => {
        setOpen(false)
        setCode('')
        setName('')
        setDescription('')
        window.location.hash = '#/questionnaire/' + r.id
      })
      .catch((err) => setSaveError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setSaving(false))
  }

  const columns: Column<Questionnaire>[] = [
    {
      key: 'code',
      header: 'Questionnaire',
      sortValue: (q) => q.code,
      render: (q) => (
        <a href={'#/questionnaire/' + encodeURIComponent(q.id)}>
          <span className="cell-primary mono">{q.code}</span>
          <span className="cell-sub">{q.name}</span>
        </a>
      ),
    },
    {
      key: 'purpose',
      header: 'Purpose',
      sortValue: (q) => q.purpose,
      render: (q) => <span className="mono">{q.purpose}</span>,
    },
    {
      key: 'state',
      header: 'State',
      sortValue: (q) => (q.isArchived ? 3 : q.draftVersionId ? 0 : q.publishedVersionId ? 1 : 2),
      render: statusPill,
    },
    {
      key: 'questions',
      header: 'Questions',
      numeric: true,
      sortValue: (q) => q.publishedQuestionCount ?? -1,
      render: (q) =>
        q.publishedQuestionCount === undefined ? (
          <span className="muted">not published</span>
        ) : (
          q.publishedQuestionCount
        ),
    },
    {
      key: 'versions',
      header: 'Versions',
      numeric: true,
      sortValue: (q) => q.versionCount,
      render: (q) => q.versionCount,
    },
    {
      key: 'published',
      header: 'Published',
      sortValue: (q) => q.publishedAt ?? '',
      render: (q) => (q.publishedAt ? shortDate(q.publishedAt) : <span className="muted">never</span>),
    },
  ]

  if (error) return <ErrorBox error={error} what="Questionnaires" />
  if (!list) return <Loading what="questionnaires" />

  const archivedCount = list.filter((q) => q.isArchived).length
  const shown = showArchived ? list : list.filter((q) => !q.isArchived)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Questionnaires</h1>
        </div>
        <div className="studio-header-actions">
          {/* Where a question's weight is actually set. It lives on the risk
              model, correctly, and having no path to it from here is why the
              weights looked absent from the questionnaire side. */}
          <a className="linkish" href="#/risk-models">
            Question weights
          </a>
          <a className="linkish" href="#/questionnaire/import">
            Import one
          </a>
          {archivedCount > 0 && (
            <button type="button" className="linkish" aria-pressed={showArchived} onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Hide archived' : 'Show archived (' + archivedCount + ')'}
            </button>
          )}
          <button
            type="button"
            className="primary"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            disabled={!can('questionnaire.author')}
            title={can('questionnaire.author') ? undefined : refusalReason('questionnaire.author')}
          >
            New questionnaire
          </button>
        </div>
      </div>

      {open && (
        <form className="panel" onSubmit={create}>
          <h2>New questionnaire</h2>
          {saveError && <ErrorBox error={saveError} what="The questionnaire" />}

          <div className="form-grid">
            <div className="field">
              <label htmlFor="q-code">Code</label>
              <input
                id="q-code"
                className="mono"
                required
                value={code}
                spellCheck={false}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                aria-describedby="q-code-note"
              />
              <p className="field-note" id="q-code-note">
                Short and stable. It appears in every export and every evidence entry.
              </p>
            </div>

            <div className="field">
              <label htmlFor="q-name">Name</label>
              <input id="q-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label htmlFor="q-desc">Description</label>
            <textarea id="q-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <fieldset className="fieldset">
            <legend>Purpose</legend>
            {PURPOSES.map((p) => (
              <label className="pickrow" key={p.value}>
                <input
                  type="radio"
                  name="purpose"
                  value={p.value}
                  checked={purpose === p.value}
                  onChange={() => setPurpose(p.value)}
                />
                <span>
                  <strong>{p.label}</strong>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="commit-bar">
            <button
              type="submit"
              className="primary"
              disabled={saving || !code.trim() || !name.trim()}
              data-action="tprm.questionnaires.a04"
            >
              {saving ? 'Creating' : 'Create and open'}
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <StandardTemplates existing={new Set((list ?? []).map((q) => q.code.toUpperCase()))} onAdded={load} />

      <DataTable
        caption="Questionnaires in this tenant"
        columns={columns}
        rows={shown}
        rowKey={(q) => q.id}
        defaultSort={{ key: 'code', dir: 'asc' }}
        empty={
          archivedCount > 0
            ? {
                title: 'Every questionnaire is archived',
                detail: 'Show the archived ones to restore one, or add a new one.',
              }
            : {
                title: 'No questionnaires yet.',
                detail: 'Nothing can be asked of a vendor until one is published.',
                action: { label: 'Import a questionnaire', href: '#/questionnaire/import' },
              }
        }
      />
    </>
  )
}
